"use client";

import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface QuantityControlProps {
  value: number;
  soldByWeight?: boolean;
  onChange: (value: number) => void | Promise<void>;
  disabled?: boolean;
  className?: string;
  compact?: boolean;
}

const MAX_QUANTITY = 999;

function normalizeQuantity(value: number, soldByWeight: boolean) {
  if (!Number.isFinite(value)) return 1;
  const normalized = soldByWeight ? Math.round(value * 1000) / 1000 : Math.round(value);
  return Math.min(MAX_QUANTITY, Math.max(soldByWeight ? 0.001 : 1, normalized));
}

export function formatQuantity(value: number, soldByWeight = false) {
  const formatted = new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: soldByWeight ? 3 : 0,
  }).format(value);
  return soldByWeight ? `${formatted} kg` : `${formatted}×`;
}

export function QuantityControl({
  value,
  soldByWeight = false,
  onChange,
  disabled = false,
  className,
  compact = false,
}: QuantityControlProps) {
  const [draft, setDraft] = useState(String(value));
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(String(value)), [value]);

  async function commit(nextValue: number) {
    const next = normalizeQuantity(nextValue, soldByWeight);
    setDraft(String(next));
    if (next === value) return;
    setSaving(true);
    try {
      await onChange(next);
    } catch {
      setDraft(String(value));
    } finally {
      setSaving(false);
    }
  }

  function commitDraft() {
    const parsed = Number(draft.replace(",", "."));
    void commit(parsed);
  }

  function draftValue() {
    const parsed = Number(draft.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : value;
  }

  const minimum = soldByWeight ? 0.001 : 1;
  const inputWidth = soldByWeight ? (compact ? "w-[68px]" : "w-[78px]") : compact ? "w-10" : "w-12";

  return (
    <div className={cn("inline-flex items-center rounded-lg border bg-background overflow-hidden", className)}>
      <button
        type="button"
        onClick={() => void commit(draftValue() - 1)}
        disabled={disabled || saving || value <= minimum}
        className={cn("inline-flex items-center justify-center hover:bg-muted disabled:opacity-40", compact ? "h-7 w-7" : "h-8 w-8")}
        aria-label={soldByWeight ? "Quitar un kilogramo" : "Quitar una unidad"}
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <div className="relative border-x">
        <input
          type="number"
          inputMode="decimal"
          min={minimum}
          max={MAX_QUANTITY}
          step={soldByWeight ? 0.001 : 1}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          disabled={disabled || saving}
          aria-label={soldByWeight ? "Cantidad en kilogramos" : "Cantidad"}
          className={cn(
            "h-7 appearance-none bg-transparent px-1 text-center text-xs font-semibold tabular-nums outline-hidden [MozAppearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
            inputWidth,
            !compact && "h-8 text-sm",
            soldByWeight && "pr-5"
          )}
        />
        {soldByWeight && (
          <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
            kg
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={() => void commit(draftValue() + 1)}
        disabled={disabled || saving || value >= MAX_QUANTITY}
        className={cn("inline-flex items-center justify-center hover:bg-muted disabled:opacity-40", compact ? "h-7 w-7" : "h-8 w-8")}
        aria-label={soldByWeight ? "Agregar un kilogramo" : "Agregar una unidad"}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
